import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Button } from 'primereact/button'
import { useMountEffect } from 'primereact/hooks'
import { assign, cloneDeep, isEmpty, max, padStart } from 'lodash'
import { CommonContext } from '../common/context/commonContext'
import {
    GoodsMoveProps,
    GoodsMoveReq,
    InventoryGoodsDetail,
    InventoryGoodsMoveDialogProps,
    LocationArea,
    LocationDetail,
    LocationPasswordReq,
} from '../../types/inventory'
import { Divider } from 'primereact/divider'
import { CommonCode } from '../../types/common'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { InputNumber } from 'primereact/inputnumber'
import { useFormik } from 'formik'
import { number, object, string } from 'yup'
import CommonProgressSpinner from '../common/CommonProgressSpinner'
import { tokens } from 'locales/tokens'
import { useTranslation } from 'react-i18next'

const GoodsMove = (props: GoodsMoveProps) => {
    const { $toast, $http, $alert } = useContext(CommonContext)
    const { t } = useTranslation()
    const [goods, setGoods] = useState<InventoryGoodsDetail>({
        rowNum: 0,
        lctSeq: '',
        goodsId: '',
        goodsNm: '',
        centerId: '',
        centerNm: '',
        invnSeq: 0,
        goodsQty: 0,
        pckGoodsQty: 0,
        avlbGoodsQty: 0,
        gdslsUnitCd: '',
        specTxt: '',
        shlfYmd: '',
        shlfYmdFm: '',
        shlfRemainDay: 0,
        strgDay: 0,
        prchUnitAmt: 0,
        imageUri: '',
        shlfYn: '',
        lotNo: '',
        sysCd: '',
    })
    const [loading, setLoading] = useState<boolean>(false)

    useMountEffect(() => {
        if (!props.data) return
        setGoods(props.data)
        formik.validateField('goodsQty')
    })

    const initialValues: GoodsMoveReq = {
        goodsQty: undefined,
    }
    const formik = useFormik({
        initialValues,
        validationSchema: object({
            goodsQty: number()
                .required(t(tokens.common.movement_quantity_is_required))
                .min(1, t(tokens.inventory.enter_minimum_move_qty_of_1_or_more))
                .max(
                    goods.avlbGoodsQty,
                    t(tokens.inventory.entered_move_qty_exceeds_movable_qty) + ':' + goods.avlbGoodsQty + `${t(tokens.common.there_are_more)}`,
                ),
        }),
        onSubmit: async (param: GoodsMoveReq) => {
            if (isEmpty(goods) || isEmpty(goods.lctSeq)) {
                $toast({ title: t(tokens.common.accept), text: t(tokens.inventory.no_inventory_items_to_move), icon: 'warning' })
                return
            }
            const isConfirm = await $alert({
                icon: 'info',
                title: t(tokens.inventory.stock_movement),
                text: goods.goodsNm + ` ${t(tokens.common.quantity_of_goods)}` + param.goodsQty + `${t(tokens.common.would_you_count_move_center)}`,
                isConfirm: true,
            })
            if (!isConfirm) return

            setLoading(true)

            const data: any = {
                goodsQty: param.goodsQty,
                lctSeq: goods.lctSeq,
                goodsId: goods.goodsId,
                invnSeq: goods.invnSeq,
            }

            console.log('onSubmit', data)

            await $http()
                .put('/inventory/request-move-goods', data)
                .then((result: any) => {
                    if (!result) return // null pass
                    if (result.succCd !== 'S') {
                        $toast({
                            title: t(tokens.common.fail),
                            icon: 'failure',
                        })
                        return
                    }
                    $toast({ title: t(tokens.common.success), text: t(tokens.inventory.complete_stock_movement), icon: 'success' })
                    setLoading(false)
                    props.onHide(true)
                })
        },
    })

    return (
        <>
            {loading && <CommonProgressSpinner withBackground={true} />}
            {goods && (
                <div className="grid">
                    <div className="col-12 flex font-bold mt-2">
                        <label className="text-color-blue text-lg required pl-2">{t(tokens.inventory.move_qty)}</label>
                    </div>
                    <div className="col-12 flex">
                        <InputNumber
                            id="goodsQty"
                            min={0}
                            placeholder={t(tokens.common.please_enter_the_quantity_to_move)}
                            inputStyle={{ paddingLeft: '1rem' }}
                            className="w-full"
                            value={formik.values.goodsQty}
                            onChange={(e) => formik.setFieldValue('goodsQty', e.value)}
                            locale="ko-KR"
                        />
                    </div>
                    <div className="col-12 flex pl-2">
                        {formik.errors.goodsQty ? (
                            <span className="ml-1 mt-1 text-red-600" style={{ fontSize: '12px' }}>
                                {formik.errors.goodsQty}
                            </span>
                        ) : null}
                    </div>
                    <form className="col-12 flex" onSubmit={formik.handleSubmit}>
                        <div className="col-6 flex pl-0">
                            <Button
                                className="w-full p-button-secondary"
                                label={t(tokens.common.close)}
                                size="large"
                                onClick={() => props.onHide(false)}
                            />
                        </div>
                        <div className="col-6 flex pr-0">
                            <Button className="w-full p-button-success" label={t(tokens.common.complete)} size="large" type="submit" />
                        </div>
                    </form>
                </div>
            )}
        </>
    )
}

export default GoodsMove
